import { useEffect, useRef, useState } from 'react'
import { client } from '../lib/api'

type ChatMessage = { role: 'user' | 'assistant'; content: string }
type TicketKind = 'support' | 'feedback' | 'bug'

const KIND_LABEL: Record<TicketKind, string> = {
  support: 'Message the team',
  feedback: 'Feedback',
  bug: 'Bug report',
}

const GREETING: ChatMessage = {
  role: 'assistant',
  content:
    'Hi! I can help with how Pairvelle works — matching, messaging, credits, verification, account standing. For anything about your specific account, use "Message the team" below and a human will follow up.',
}

export function Support({ notify }: { notify: (msg: string) => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const [kind, setKind] = useState<TicketKind>('support')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)

  const [tickets, setTickets] = useState<
    { id: string; kind: string; subject: string | null; status: string; admin_reply: string | null; created_at: string }[]
  >([])

  const loadTickets = () =>
    client
      .myTickets()
      .then((r) => setTickets(r.tickets))
      .catch(() => undefined)

  useEffect(() => {
    void loadTickets()
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, thinking])

  const send = async () => {
    const text = input.trim()
    if (!text || thinking) return
    const next: ChatMessage[] = [...messages, { role: 'user', content: text }]
    setMessages(next)
    setInput('')
    setThinking(true)
    try {
      // Greeting is client-side only — send just the real conversation
      const res = await client.supportChat(next.slice(1))
      setMessages([...next, { role: 'assistant', content: res.reply }])
    } catch (e) {
      notify((e as Error).message)
      setMessages(next)
    } finally {
      setThinking(false)
    }
  }

  const submitTicket = async () => {
    if (body.trim().length < 5) {
      notify('Please describe your issue first')
      return
    }
    setSending(true)
    try {
      const res = await client.submitTicket({ kind, subject: subject.trim() || undefined, body: body.trim() })
      notify(res.message)
      setSubject('')
      setBody('')
      await loadTickets()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="support-layout">
      <div className="support-chat-card">
        <strong>Ask the assistant</strong>
        <p className="page-sub">Instant answers about how Pairvelle works. No account access.</p>
        <div className="support-chat-scroll" ref={scrollRef}>
          {messages.map((m, i) => (
            <div key={i} className={`support-bubble ${m.role}`}>
              {m.content}
            </div>
          ))}
          {thinking && <div className="support-bubble assistant thinking">…</div>}
        </div>
        <div className="support-chat-input">
          <input
            className="input"
            placeholder="e.g. How do credits work?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void send()
            }}
          />
          <button className="button primary" onClick={() => void send()} disabled={thinking || !input.trim()}>
            Send
          </button>
        </div>
      </div>

      <div className="support-ticket-card">
        <strong>Contact a human</strong>
        <p className="page-sub">
          Account issues, appeals, refunds, feedback, and bugs go to the team. We reply by email.
        </p>
        <div className="segmented support-segmented">
          {(Object.keys(KIND_LABEL) as TicketKind[]).map((k) => (
            <button key={k} type="button" className={kind === k ? 'selected' : ''} onClick={() => setKind(k)}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <input
            className="input"
            placeholder={kind === 'bug' ? 'What broke? (short summary)' : 'Subject (optional)'}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <div className="field">
          <textarea
            className="input"
            rows={4}
            placeholder={
              kind === 'bug'
                ? 'What did you do, what did you expect, and what happened instead?'
                : kind === 'feedback'
                  ? 'What should we improve?'
                  : 'How can we help?'
            }
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <button className="button primary" onClick={() => void submitTicket()} disabled={sending}>
          {sending ? 'Sending…' : `Send ${kind === 'support' ? 'message' : KIND_LABEL[kind].toLowerCase()}`}
        </button>
      </div>

      {tickets.length > 0 && (
        <div className="support-ticket-card">
          <strong>Your messages</strong>
          <ul className="support-ticket-list">
            {tickets.map((t) => (
              <li key={t.id}>
                <div className="support-ticket-head">
                  <span className={`ticket-status ${t.status}`}>{t.status.replace('_', ' ')}</span>
                  <span className="ticket-kind">{t.kind}</span>
                  <span className="ticket-date">{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
                <p>{t.subject ?? '(no subject)'}</p>
                {t.admin_reply && <p className="ticket-reply">Team: {t.admin_reply}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
